import React from "react";
import { tono } from "./tonos";

/**
 * Matriz de calor del kit de tableros: filas × columnas con un número por
 * celda, teñida con el tono de la columna (más intensa cuanto más pesa la
 * celda). Cada celda es un filtro que abre el tracking (nunca lista filas).
 * @author Diego Serrano <diego.serrano@repremundo.com.co>
 * @since 2026-09-15
 * @param {string} titulo     Título de la tarjeta
 * @param {string} subtitulo  Nota bajo el título
 * @param {Array}  columnas   [{clave, etiqueta, tonoxxx}]
 * @param {Array}  filas      [{clave, etiqueta, celdas: {columna: {N, IDS}}, total: {N, IDS}}]
 * @param {function} onCelda  (fila, columna, corte) al hacer clic en una celda o en el total
 * @param {string} vacio      Texto cuando no hay filas con datos
 * @param {string} etiquetaFilas Encabezado de la primera columna (lo que es cada fila)
 * @param {string} claveActiva  Clave de la fila a resaltar (ninguna = vacio)
 */
const Matrizxx = ({ titulo, subtitulo, columnas = [], filas = [], onCelda, vacio = "Nada en este corte", etiquetaFilas = "Estado", claveActiva = "" }) => {
  const nTotal = filas.reduce((acc, f) => acc + Number(f.total?.N ?? 0), 0);
  const max = Math.max(1, ...filas.flatMap((f) => columnas.map((c) => Number(f.celdas?.[c.clave]?.N ?? 0))));
  const estilo = (corte, tonoxxx) => {
    const n = Number(corte?.N ?? 0);
    const t = tono(tonoxxx);
    if (n === 0) return { background: "#FFFFFF", color: "#C7CDD4", border: "1px solid #EEF0F3" };
    if (n / max >= 0.66) return { background: t.hex, color: "#FFFFFF" };
    return { background: t.chip.background, color: t.chip.color };
  };
  return (
    <div className="tbl-card" style={{ gap: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12 }}>
        <span className="tbl-card-title">{titulo}</span>
        {subtitulo && <span className="tbl-kpi-nota">{subtitulo}</span>}
      </div>
      {nTotal === 0 ? (
        <span className="tbl-estado">{vacio}</span>
      ) : (
        <div className="tbl-matriz" style={{ gridTemplateColumns: `minmax(180px, 1.6fr) repeat(${columnas.length}, minmax(0, 1fr)) 64px` }}>
          <span className="tbl-matriz-col" style={{ textAlign: "left" }}>{etiquetaFilas}</span>
          {columnas.map((c) => (
            <span key={c.clave} className="tbl-matriz-col" style={{ color: tono(c.tonoxxx).chip.color }}>{c.etiqueta}</span>
          ))}
          <span className="tbl-matriz-col" style={{ color: "#0D3C60" }}>Total</span>
          {filas.map((f) => (
            <React.Fragment key={f.clave}>
              <span className={`tbl-matriz-fila ${String(f.clave) === String(claveActiva) ? "activa" : ""}`}>{f.etiqueta}</span>
              {columnas.map((c) => {
                const corte = f.celdas?.[c.clave] ?? { N: 0, IDS: [] };
                return (
                  <button key={c.clave} type="button" className={`tbl-matriz-celda ${String(f.clave) === String(claveActiva) ? "activa" : ""}`} style={estilo(corte, c.tonoxxx)} disabled={Number(corte.N) === 0} onClick={() => onCelda?.(f, c, corte)} title={`${f.etiqueta} · ${c.etiqueta}`}>
                    {Number(corte.N ?? 0)}
                  </button>
                );
              })}
              <button type="button" className="tbl-matriz-celda" style={{ background: "#E6EDF3", color: "#0D3C60" }} disabled={Number(f.total?.N ?? 0) === 0} onClick={() => onCelda?.(f, null, f.total)} title={`${f.etiqueta} · todas`}>
                {Number(f.total?.N ?? 0)}
              </button>
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

export default Matrizxx;
